import clsx from "clsx";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Box, Fab, Tooltip, useTheme } from "@mui/material";
import {
  KeyboardDoubleArrowRightRounded,
  MoreVert,
  PostAdd,
} from "@mui/icons-material";

import "components/pages/transmissions/modal/detail/pdf/PdfContainer.css";

import {
  setShowActionMenu,
  toggleDrawer,
} from "app/store/transmission/slice/transmissionSlice";

import LoadingModal from "components/common/loading/LoadingModal";
import Notes from "components/pages/transmissions/modal/detail/notes/Notes";
import PatientInfo from "components/pages/transmissions/modal/detail/info/PatientInfo";
import PdfViewer from "components/pages/transmissions/modal/detail/pdf/viewer/PdfViewer";

import { HF_BILLING_CODES, USER_ROLES } from "static/constants";
import ActionMenu from "components/common/action-menu/ActionMenu";
import InsertPageModal from "./modals/InsertPage/InsertPageModal";
import ReportTitle from "./ReportTitle";
import PdfViewerControls from "./viewer/PdfViewerControls";
import ReportStatus from "./ReportStatus";
import OtherReports from "./OtherReports";

const PdfContainer = ({
  data,
  isFetching,
  isRegularPdfLoading,
  launchSnackbarMessage,
  openRegularReportOnNewTab,
}) => {
  const theme = useTheme();
  const {
    pdfId,
    currentPageNumber,
    openDrawer,
    totalPdfPages,
    showNotes,
    reportId,
    reportDetail,
    showActionMenu,
    transmissionReportPdfUrl,
    fileToAttachToReport,
  } = useSelector((state) => state.transmission);

  const { currentUser } = useSelector((state) => state.auth);
  const { isArchiveReport } = useSelector((state) => state.archiveReport);

  const [zoomLevel, setZoomLevel] = useState(1);
  const handleSelectZoom = (value) => {
    setZoomLevel(parseFloat(value));
  };

  const dispatch = useDispatch();

  const actionMenuOptions = [
    {
      label: (
        <div className="d-flex align-items-center">
          <PostAdd className="mr-1" />
          Insert pages to report
        </div>
      ),
      isDisabled: !transmissionReportPdfUrl,
      action: () => dispatch(setShowActionMenu(true)),
    },
  ];

  const renderReportModifiedTooltipContent = () => (
    <span>
      {fileToAttachToReport?.modifiedAt && (
        <p>Last modified: {fileToAttachToReport?.modifiedAt}</p>
      )}
      {fileToAttachToReport?.pageInsertFrom && (
        <p>
          Added Page Numbers: {fileToAttachToReport?.pageInsertFrom}
          {fileToAttachToReport?.pageInsertTo
            ? "-" + fileToAttachToReport?.pageInsertTo
            : ""}
        </p>
      )}
    </span>
  );

  return (
    <>
      <div
        id={`pdf-${pdfId}`}
        className="view-pdf view-pdf--page"
        style={{
          backgroundColor: showNotes
            ? theme.palette.background.default
            : theme.palette.background.paper,
        }}
      >
        {showNotes && (
          <Tooltip title="Open Patient Info">
            <Fab
              variant="extended"
              aria-label="open drawer"
              edge="start"
              onClick={() => dispatch(toggleDrawer(true))}
              style={{
                display: openDrawer ? "none" : "flex",
                position: "absolute",
                top: currentPageNumber && totalPdfPages ? "37px" : "28px",
                left: "5px",
                height: "40px",
                minWidth: "40px",
                padding: "0",
                border: "1px solid #ccc",
                color: "#949494",
                backgroundColor: theme.palette.background.paper,
                borderRadius: "4px",
                zIndex: "99999",
                boxShadow: "none",
              }}
            >
              <KeyboardDoubleArrowRightRounded />
            </Fab>
          </Tooltip>
        )}

        <Box
          className={clsx("view-pdf--content-wrapper", {
            "view-pdf--with-drawer": openDrawer,
            "view-pdf--with-notes": showNotes,
          })}
        >
          <Box
            className="view-pdf__drawer"
            style={{
              display: openDrawer ? "block" : "none",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {data && <PatientInfo data={data} />}
          </Box>

          <Box
            className="view-pdf__main"
            style={{ backgroundColor: theme.palette.background.paper }}
          >
            {showNotes && data?.transmissionType !== "connectivityStatus" && (
              <div
                className="view-pdf__header"
                style={{ paddingLeft: openDrawer ? "16px" : "35px" }}
              >
                <div
                  className="d-flex flex-column"
                  style={{ gap: "4px", width: "65%" }}
                >
                  <ReportTitle data={data} openDrawer={openDrawer} />

                  <PdfViewerControls
                    currentPageNumber={currentPageNumber}
                    totalPdfPages={totalPdfPages}
                    zoomLevel={zoomLevel}
                    handleSelectZoom={handleSelectZoom}
                  />

                  <ReportStatus
                    reportDetail={reportDetail}
                    currentUser={currentUser}
                    fileToAttachToReport={fileToAttachToReport}
                    renderReportModifiedTooltipContent={
                      renderReportModifiedTooltipContent
                    }
                    data={data}
                  />
                </div>
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center">
                    <OtherReports
                      launchSnackbarMessage={launchSnackbarMessage}
                      reportDetail={reportDetail}
                      openRegularReportOnNewTab={openRegularReportOnNewTab}
                      HF_BILLING_CODES={HF_BILLING_CODES}
                    />

                    {!isArchiveReport &&
                      [
                        USER_ROLES.ADMIN,
                        USER_ROLES.CLINIC_ADMIN,
                        USER_ROLES.USER,
                      ].includes(currentUser.role) && (
                        <ActionMenu
                          icon={<MoreVert />}
                          menuItems={actionMenuOptions}
                        />
                      )}
                  </div>
                </div>
              </div>
            )}
            <PdfViewer
              fillContainer
              reportId={reportId}
              launchSnackbarMessage={launchSnackbarMessage}
              zoomLevel={zoomLevel}
            />
          </Box>

          {isRegularPdfLoading && (
            <LoadingModal
              open={isRegularPdfLoading}
              loadingText="Regular Report Loading..."
            />
          )}
          {showNotes && (
            <Box className="view-pdf__notes--wrapper">
              <Notes
                data={data}
                isFetching={isFetching}
                launchSnackbarMessage={launchSnackbarMessage}
              />
            </Box>
          )}
        </Box>
      </div>
      {showActionMenu && <InsertPageModal />}
    </>
  );
};

export default PdfContainer;
